import React, { createContext, useContext, useEffect, useState } from 'react';
import { translations, type Language, type TranslationKey } from '../lib/i18n';

export type ThemeChoice = 'light'; // | 'dark' | 'system';
export type TextSize = 'compact' | 'comfortable' | 'large';
export type DefaultPrivacy = 'family' | 'private';

interface Settings {
  theme: ThemeChoice;
  language: Language;
  textSize: TextSize;
  defaultPrivacy: DefaultPrivacy;
}

const DEFAULT_SETTINGS: Settings = {
  theme: 'light',
  language: 'en',
  textSize: 'comfortable',
  defaultPrivacy: 'family',
};

const STORAGE_KEY = 'mtt_settings';

const TEXT_SIZE_PX: Record<TextSize, string> = {
  compact: '15px',
  comfortable: '16px',
  large: '18px',
};

function loadSettings(): Settings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_SETTINGS, ...parsed, theme: 'light' };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

/*
function systemPrefersDark(): boolean {
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}
*/

interface SettingsContextValue {
  settings: Settings;
  updateSettings: (updates: Partial<Settings>) => void;
  t: (key: TranslationKey) => string;
}

const SettingsContext = createContext<SettingsContextValue | null>(null);

export function SettingsProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettings] = useState<Settings>(loadSettings);

  // Apply visual side effects (light mode only for now, dark/system commented out)
  useEffect(() => {
    document.documentElement.classList.remove('dark');
    /*
    const applyDark = () => {
      const isDark = settings.theme === 'dark' || (settings.theme === 'system' && systemPrefersDark());
      document.documentElement.classList.toggle('dark', isDark);
    };
    applyDark();

    if (settings.theme !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    mq.addEventListener('change', applyDark);
    return () => mq.removeEventListener('change', applyDark);
    */
  }, [settings.theme]);

  useEffect(() => {
    document.documentElement.style.setProperty('--font-size', TEXT_SIZE_PX[settings.textSize]);
  }, [settings.textSize]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  }, [settings]);

  const updateSettings = (updates: Partial<Settings>) => {
    setSettings(prev => ({ ...prev, ...updates }));
  };

  const t = (key: TranslationKey): string => {
    return translations[settings.language][key] ?? translations.en[key] ?? key;
  };

  return (
    <SettingsContext.Provider value={{ settings, updateSettings, t }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings(): SettingsContextValue {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error('useSettings must be used within SettingsProvider');
  return ctx;
}
